// The control plane refuses longer ones (UserAppManifestRules).
const MAX_NAME_CHARS = 60;
const MAX_TAGLINE_CHARS = 140;
const MAX_NAME_WORDS = 4;

const POLITE = new Set(['please', 'can', 'could', 'would', 'you', 'i', 'we', 'want', 'need', 'like', 'to']);
const VERBS = new Set(['create', 'make', 'build', 'generate', 'design', 'write']);
const ARTICLES = new Set(['a', 'an', 'the', 'some']);
const APP_WORDS = new Set(['app', 'application', 'tool']);
const APP_LINKS = new Set(['that', 'to', 'for', 'which']);
const DETAILS = new Set(['that', 'which', 'who', 'where', 'with', 'so', 'and', 'but', 'also', 'i', 'we', 'you', 'it', 'using', 'please']);
const TRAILING = new Set(['a', 'an', 'the', 'of', 'in', 'on', 'for', 'my', 'your', 'some', 'to', 'and']);
const SMALL = new Set(['a', 'an', 'the', 'of', 'in', 'on', 'for', 'to', 'and', 'or', 'with']);

const lower = (word: string | undefined) => word?.toLowerCase() ?? '';

const dropWhile = (words: string[], set: ReadonlySet<string>): string[] => {
  const first = words.findIndex((word) => !set.has(lower(word)));
  return first === -1 ? [] : words.slice(first);
};

const dropRequest = (words: string[]): string[] => {
  let rest = dropWhile(words, POLITE);
  if (lower(rest[0]) === 'give' && lower(rest[1]) === 'me') rest = rest.slice(2);
  else if (VERBS.has(lower(rest[0]))) rest = rest.slice(1);
  rest = dropWhile(rest, ARTICLES);
  return APP_WORDS.has(lower(rest[0])) && APP_LINKS.has(lower(rest[1])) ? rest.slice(2) : rest;
};

const upToFirst = (words: string[], stop: ReadonlySet<string>): string[] => {
  const end = words.findIndex((word, index) => index > 0 && stop.has(lower(word)));
  return end === -1 ? words : words.slice(0, end);
};

const titleCase = (word: string, index: number): string =>
  index > 0 && SMALL.has(lower(word)) ? lower(word) : word.charAt(0).toUpperCase() + word.slice(1);

/** A short Title Case name guessed from the first prompt, until the assistant picks a better one. */
export const nameFromPrompt = (prompt: string): string | null => {
  const clause = prompt.trim().split(/[\n.!?,;:()]/)[0] ?? '';
  const subject = upToFirst(upToFirst(dropRequest(clause.split(/\s+/).filter(Boolean)), DETAILS), APP_WORDS);
  let words = subject.slice(0, MAX_NAME_WORDS);
  while (words.length > 1 && TRAILING.has(lower(words.at(-1)))) words = words.slice(0, -1);
  if (words.length === 0 || (words.length === 1 && APP_WORDS.has(lower(words[0])))) return null;
  return words.map((word, index) => titleCase(word, index)).join(' ').slice(0, MAX_NAME_CHARS);
};

export const taglineFromPrompt = (prompt: string): string | null => {
  const firstLine = prompt.trim().split('\n')[0] ?? '';
  const end = firstLine.search(/[.!?](?=\s|$)/);
  const sentence = (end === -1 ? firstLine : firstLine.slice(0, end + 1)).replace(/\s+/g, ' ').trim();
  if (!sentence) return null;
  if (sentence.length <= MAX_TAGLINE_CHARS) return sentence;
  const cut = sentence.slice(0, MAX_TAGLINE_CHARS - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};
